import { appUtils } from 'lib/AppUtils'
import Link from 'next/link'
import { FC, Fragment, useEffect, useState } from 'react'
import useSWRImmutable from 'swr/immutable'

import { useCarousel } from '@/hooks/Carousel'
import { useLanguage, useLayout } from '@/hooks/Contexts'
import { BannerItem } from '@/models/Index'
import { API_URL } from '@/utils/Consts'

import { CompetitionCountdown } from '../Competition'

/**
 * 按断点挑出该显示的那几张 banner。
 *
 * 必须是模块级纯函数,不能内联进 effect:初始 state 和 effect 两处都要用它,
 * 一旦逻辑分叉就会出 hydration 不一致。
 */
const pickBannersForBreakpoint = (banners: BannerItem[], breakpoint: string): BannerItem[] => {
  if (!Array.isArray(banners) || banners.length === 0) {
    return []
  }
  // breakpoint 在首帧是 undefined(见下方 useState 注释),两个 includes 都落空,
  // 于是走到 'tablet' —— 中间尺寸,放大缩小都不至于太糟,是视口未知时最稳的一档。
  const keyword = breakpoint?.includes?.('xl') ? 'desktop' : breakpoint?.includes?.('xs') ? 'mobile' : 'tablet'
  const filtered = banners.filter(item => item?.image_url?.toLowerCase?.()?.includes?.(keyword))
  return !appUtils.isEmpty(filtered) ? filtered : banners
}

export const BannerSlider: FC<{ initialBanners?: BannerItem[] }> = ({ initialBanners }) => {
  const { breakpoint } = useLayout()
  // 初始值必须从 props 同步派生,不能是 undefined 等 effect 来填:effect 在服务端不跑,
  // 那样服务端渲染出的就是兜底图 banner_home.webp,而真实 banner 数据明明已经随
  // __NEXT_DATA__ 发下来了。浏览器先画兜底图、hydrate 后才换成真图 —— 这就是刷新时
  // 看到的那一瞬间闪烁,同时也让 pages/index.tsx 里那次 SSR 预取完全白做。
  //
  // 服务端不知道视口。_app.tsx:52 的 layout 初始值是 `{} as Layout`(不是
  // mobileLayout),所以首帧 breakpoint 是 undefined,pickBannersForBreakpoint 会落到
  // 'tablet'。服务端和客户端首帧算出来的是同一个值,不会 hydration 报错;等 _app 的
  // resize listener 量出真实断点后,下面的 effect 再换成对应尺寸的图。
  const [shownBanners, setShownBanners] = useState<BannerItem[]>(() => pickBannersForBreakpoint(initialBanners, breakpoint))
  // 只有真拿到 SSR 预取数据时才传 fallbackData。不能无条件传:SWR 判「要不要在挂载时拉取」
  // 用的是严格 undefined 检查(isUndefined(data) || revalidateIfStale),fallbackData 传 null
  // 时 data 是 null 而非 undefined,再叠加 useSWRImmutable 的 revalidateIfStale:false,
  // 客户端就永远不会发起请求 —— SSR 预取失败时 banner 区会卡在静态兜底图上。
  const { isValidating, isLoading, data } = useSWRImmutable(API_URL.banners, appUtils.fetcher, initialBanners ? { fallbackData: initialBanners } : undefined)
  useEffect(() => {
    if (breakpoint && !isValidating && !isLoading) {
      setShownBanners(prev => {
        const next = pickBannersForBreakpoint(data, breakpoint)
        // 内容没变就返回原引用,避免 hydrate 后立刻多一次无谓的重渲染
        return prev?.length === next.length && prev.every((item, i) => item?.image_url === next[i]?.image_url) ? prev : next
      })
    }
  }, [breakpoint, isValidating, isLoading, data])
  return shownBanners?.length > 0 ? (
    <BannerSliderContent banners={shownBanners} />
  ) : (
    <div className='mt-1 w-screen bg-[#050601] md:mt-20'>
      <div className={`main-container h-[490px] ${isValidating || isLoading ? 'animate-pulse bg-[#050601] dark:bg-black' : ''}`}>
        {isValidating || isLoading ? null : ( // <ImgSkeleton className='h-12 w-12 text-neutral-500 dark:text-neutral-600' />
          <img
            src='/assets/banner_home.webp'
            alt='R Kings Competitions — win cars, cash and luxury prizes'
            className='pointer-events-none h-auto w-full animate-fade-in-down object-cover mix-blend-lighten'
          />
        )}
      </div>
    </div>
  )
}

const BannerSliderContent: FC<{ banners: Array<BannerItem> }> = ({ banners }) => {
  const { isDesktop } = useLayout()
  const {
    currentIndex,
    next,
    previous,
    gotoIndex,
    setMouseEnter,
    handleMouseLeave,
    handleMouseMove,
    handleMouseUp,
    handleMouseDown,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  } = useCarousel(banners || [], 5000, isDesktop)
  // currentIndex 存在 useCarousel 的 state 里,不随 banners 变化重置。而 banners 会在
  // 首帧之后换一次(服务端按未知视口出 tablet,客户端量出真实断点后换 desktop/mobile)。
  // 各断点的变体数量正常是相等的,真要不等(某个 banner 缺了某个尺寸)就会出现
  // currentIndex 越界、一张都不显示。兜一下,越界就退回第一张。
  const safeIndex = currentIndex < banners.length ? currentIndex : 0
  return (
    <Fragment>
      <div className='relative mt-16 h-full w-full animate-fade-in-down overflow-hidden bg-black md:mt-20'>
        <div className='main-container flex flex-col !px-0 xl:!px-4'>
          {banners?.length > 0
            ? banners.map((item, index) => (
                // object-fit 不是 Tailwind 的类（只有 object-cover / contain / fill /
                // none / scale-down），原来那个写了等于没写，落回浏览器默认的
                // object-fit: fill —— 图片被硬拉满 h-full w-full，横向变形。
                // 兜底图用的就是 object-cover，这里保持一致。
                <Link href={item.details} key={index} passHref>
                  <a
                    className={`h-[320px] w-full cursor-pointer select-none sm:h-[350px] xl:h-[323px] ${
                      safeIndex === index ? 'animate-fade-in-right' : 'hidden'
                    }`}
                  >
                    <img
                      onMouseEnter={() => setMouseEnter(true)}
                      onMouseLeave={handleMouseLeave}
                      onMouseDown={handleMouseDown}
                      onMouseUp={handleMouseUp}
                      onMouseMove={handleMouseMove}
                      onTouchStart={handleTouchStart}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}
                      onTouchCancel={handleTouchEnd}
                      src={item.image_url}
                      className='h-full w-full object-cover'
                      alt={item.image_url}
                      draggable={false}
                      onDragStart={e => {
                        //equivalent of draggable={false} in Firefox
                        e.preventDefault()
                      }}
                    />
                  </a>
                </Link>
              ))
            : null}
        </div>

        {banners?.length > 1 ? (
          <Fragment>
            <button aria-label='Previous slide' className='group absolute left-4 top-1/2 z-10 -mt-4 hidden md:left-8 xl:flex' onClick={previous}>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='h-6 w-6 text-white/100 group-hover:text-cyan-v1'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 16l-4-4m0 0l4-4m-4 4h18' />
              </svg>
            </button>
            <button aria-label='Next slide' className='group absolute right-4 top-1/2 z-10 -mt-4 hidden md:right-8 xl:flex' onClick={next}>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='h-6 w-6 text-white/100 group-hover:text-cyan-v1'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M17 8l4 4m0 0l-4 4m4-4H3' />
              </svg>
            </button>
            <div className='absolute bottom-0 w-full py-1 md:py-2'>
              <div className='flex justify-center px-4 xl:container xl:!px-8'>
                {banners.map((item, index) => (
                  <button
                    aria-label={`Go to slide ${index}`}
                    key={index}
                    onMouseEnter={() => setMouseEnter(true)}
                    onMouseLeave={() => setMouseEnter(false)}
                    onClick={() => {
                      if (safeIndex !== index) {
                        gotoIndex(index)
                      }
                    }}
                    className={`mr-1 h-1 w-6 border border-neutral-600 md:w-7 ${index === safeIndex ? 'bg-white/100' : 'bg-transparent'}`}
                  />
                ))}
              </div>
            </div>
          </Fragment>
        ) : null}
      </div>

      {banners?.[safeIndex]?.title ? <Timer banner={banners?.[safeIndex]} /> : null}
    </Fragment>
  )
}

const Timer: FC<{ banner: BannerItem }> = ({ banner }) => {
  const { competitions: compStr } = useLanguage()
  return (
    <div className={`fluid-gray animate-fade-in-down bg-[url('/assets/bg-black2.png')] bg-cover bg-no-repeat`}>
      <div className='container-gray my-1.5 grid grid-cols-2 items-center justify-items-center gap-y-4 text-white/100 md:justify-items-stretch'>
        <div className='col-span-full text-xl md:col-span-1 md:!leading-9 lg:text-3xl'>{banner.title}</div>
        <CompetitionCountdown endDate={banner.end_time} light extraCls='col-span-full md:col-span-1 md:row-span-2' />
        <a href={banner.details} className='btn-rd-amber btn-hover-amber btn-width col-span-full flex items-center justify-center py-2 md:col-span-1'>
          {compStr.details}
        </a>
      </div>
    </div>
  )
}

export default BannerSlider
